import React, { useState, useRef } from 'react';
import { getInitials } from '../services/presenceService';

/**
 * PresenceBadges - shows avatar stack of users currently viewing/editing.
 * @param {Array} presenceList - list of presence objects from Supabase
 * @param {string} currentUserId - the current user's id (to label "You")
 * @param {number} maxVisible - max avatars to show before +N overflow
 * @param {string} label - optional label like "viewing" or "editing"
 */
const PresenceBadges = ({ presenceList = [], currentUserId, maxVisible = 4, label = 'viewing' }) => {
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  if (!presenceList || presenceList?.length === 0) return null;

  // Deduplicate by userId (keep latest)
  const seen = new Set();
  const unique = [];
  for (const p of presenceList) {
    if (!seen?.has(p?.userId)) {
      seen?.add(p?.userId);
      unique?.push(p);
    }
  }

  const visible = unique?.slice(0, maxVisible);
  const overflow = unique?.length - maxVisible;

  const getName = (p) => {
    if (p?.userId === currentUserId) return 'You';
    return p?.nickname || p?.fullName || p?.email || 'Unknown';
  };

  return (
    <div className="flex items-center gap-1.5" title={`${unique?.length} ${label}`}>
      {/* Live pulse dot */}
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
      </span>
      {/* Avatar stack */}
      <div className="flex -space-x-1.5">
        {visible?.map((p, i) => (
          <div
            key={p?.userId || i}
            className="relative"
            onMouseEnter={() => setTooltip(p?.userId)}
            onMouseLeave={() => setTooltip(null)}
          >
            {p?.avatarUrl ? (
              <img
                src={p?.avatarUrl}
                alt={getName(p)}
                className="w-6 h-6 rounded-full border-2 border-white object-cover"
                style={{ zIndex: maxVisible - i }}
              />
            ) : (
              <div
                className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-white text-[9px] font-bold select-none"
                style={{ backgroundColor: p?.color || '#6366f1', zIndex: maxVisible - i }}
              >
                {getInitials(p)}
              </div>
            )}

            {/* Tooltip */}
            {tooltip === p?.userId && (
              <div
                ref={tooltipRef}
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 whitespace-nowrap bg-gray-900 text-white text-xs rounded px-2 py-1 pointer-events-none shadow-lg"
              >
                <span className="font-medium">{getName(p)}</span>
                <span className="text-gray-300 ml-1">{label}</span>
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            )}
          </div>
        ))}

        {/* Overflow badge */}
        {overflow > 0 && (
          <div
            className="w-6 h-6 rounded-full border-2 border-white bg-gray-400 flex items-center justify-center text-white text-[9px] font-bold select-none"
            title={`+${overflow} more`}
          >
            +{overflow}
          </div>
        )}
      </div>
      {/* Count label */}
      <span className="text-xs text-muted-foreground hidden sm:inline">
        {unique?.length === 1 && unique?.[0]?.userId === currentUserId
          ? null
          : `${unique?.length} ${label}`}
      </span>
    </div>
  );
};

export default PresenceBadges;
